import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { MessageSquare, Send, Inbox, Mail, Loader2, Plus, Paperclip, X, FileText, Sparkles, CheckCircle2, AlertCircle, XCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import api from "../../lib/api";

const SOURCE_LABEL = {
  outbound_order: "Pedido enviado",
  outbound_reminder: "Recordatorio enviado",
  manual: "Respuesta registrada",
  resend_webhook: "Respuesta recibida",
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * Dialog que muestra el hilo proveedor↔restaurante para un pedido y permite
 * registrar respuestas manualmente. Cuando MX esté configurado, los replies
 * por email aparecerán automáticamente vía webhook de Resend.
 */
export default function OrderMessagesDialog({ open, onOpenChange, restaurantId, order, onPrefilledReceive }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bodyText, setBodyText] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [ocrResults, setOcrResults] = useState({});  // {filename: result}
  const [ocrLoading, setOcrLoading] = useState(null); // filename being processed
  const fileInputRef = useRef(null);

  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

  const load = useCallback(async () => {
    if (!restaurantId || !order?.order_id) return;
    setLoading(true);
    try {
      const res = await api.request(
        `/restaurants/${restaurantId}/orders/${order.order_id}/messages`,
      );
      setMessages(res?.messages || []);
    } catch {
      toast.error("No se pudieron cargar los mensajes");
    } finally {
      setLoading(false);
    }
  }, [restaurantId, order?.order_id]);

  useEffect(() => {
    if (open) {
      load();
      setShowForm(false);
      setBodyText("");
      setFromEmail("");
      setAttachments([]);
    }
  }, [open, load]);

  const handleAttachFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Máximo 8MB por adjunto");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BACKEND}/api/restaurants/${restaurantId}/orders/${order.order_id}/messages/upload-attachment`,
        {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || "Error subiendo adjunto");
      }
      const data = await res.json();
      setAttachments((prev) => [...prev, data]);
      toast.success("Adjunto subido");
    } catch (err) {
      toast.error(err?.message || "No se pudo subir");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (filename) => {
    setAttachments((prev) => prev.filter((a) => a.filename !== filename));
  };

  const runOcrMatch = async (filename) => {
    if (!restaurantId || !order?.order_id) return;
    setOcrLoading(filename);
    try {
      const res = await api.request(
        `/restaurants/${restaurantId}/orders/${order.order_id}/attachments/${filename}/ocr-match`,
        { method: "POST" },
      );
      setOcrResults((prev) => ({ ...prev, [filename]: res }));
      const matched = res?.matched_count || 0;
      const total = res?.total_detected || 0;
      toast.success(`Detectados ${total} ítems · ${matched} coinciden con el pedido`);
    } catch (e) {
      toast.error(e?.message || "OCR no disponible");
    } finally {
      setOcrLoading(null);
    }
  };

  const submitManual = async () => {
    const txt = bodyText.trim();
    if (txt.length < 1) {
      toast.error("Escribe el contenido de la respuesta");
      return;
    }
    setSubmitting(true);
    try {
      await api.request(
        `/restaurants/${restaurantId}/orders/${order.order_id}/messages/manual-reply`,
        {
          method: "POST",
          body: JSON.stringify({
            body_text: txt,
            from_email: fromEmail.trim() || null,
            attachments: attachments.length ? attachments : null,
          }),
        },
      );
      toast.success("Respuesta registrada en el hilo");
      setBodyText("");
      setFromEmail("");
      setAttachments([]);
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.message || "No se pudo registrar la respuesta");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="order-messages-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-emerald-700" />
            Hilo del pedido
          </DialogTitle>
          <DialogDescription>
            {order?.supplier_name || "Sin proveedor"} · #{(order?.order_id || "").slice(-8)}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            {messages.length === 0 && (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Sin mensajes todavía. Cuando envíes el pedido o un recordatorio aparecerán aquí.
              </div>
            )}
            {messages.map((m) => {
              const isOut = m.direction === "out";
              return (
                <div
                  key={m.message_id}
                  className={`flex gap-3 ${isOut ? "" : "flex-row-reverse"}`}
                  data-testid={`thread-msg-${m.message_id}`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isOut ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"
                    }`}
                  >
                    {isOut ? <Send className="w-4 h-4" /> : <Inbox className="w-4 h-4" />}
                  </div>
                  <div
                    className={`flex-1 rounded-lg p-3 border text-sm ${
                      isOut ? "bg-blue-50/40 dark:bg-blue-950/20" : "bg-emerald-50/40 dark:bg-emerald-950/20"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <Badge variant="outline" className="text-[10px]">
                        {SOURCE_LABEL[m.source] || m.source}
                      </Badge>
                      <span className="text-[11px] text-muted-foreground">
                        {fmtDateTime(m.received_at || m.created_at)}
                      </span>
                    </div>
                    {m.subject && (
                      <p className="font-medium text-xs text-foreground/80 mb-1">{m.subject}</p>
                    )}
                    {(m.from_email || m.to_email) && (
                      <p className="text-[11px] text-muted-foreground mb-2 flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {isOut
                          ? `→ ${m.to_email || "—"}`
                          : `de ${m.from_email || "proveedor"}`}
                      </p>
                    )}
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
                      {m.body_text || "—"}
                    </p>
                    {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                      <div className="mt-3 grid grid-cols-3 gap-2">
                        {m.attachments.map((att) => {
                          const url = `${BACKEND}${att.url}`;
                          const isImg = (att.content_type || "").startsWith("image/");
                          return (
                            <a
                              key={att.filename}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block border rounded overflow-hidden hover:opacity-80 transition-opacity bg-muted"
                              data-testid={`thread-attachment-${att.filename}`}
                            >
                              {isImg ? (
                                <img
                                  src={url}
                                  alt={att.original_name || att.filename}
                                  className="w-full h-20 object-cover"
                                />
                              ) : (
                                <div className="w-full h-20 flex flex-col items-center justify-center text-muted-foreground p-2">
                                  <FileText className="w-5 h-5 mb-1" />
                                  <span className="text-[10px] truncate w-full text-center">
                                    {att.original_name || att.filename}
                                  </span>
                                </div>
                              )}
                            </a>
                          );
                        })}
                      </div>
                    )}
                    {/* OCR match button — only on inbound messages with attachments */}
                    {!isOut && Array.isArray(m.attachments) && m.attachments.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {m.attachments.map((att) => {
                          const cacheKey = att.filename.replace(/\./g, "__");
                          const cached = (m.ocr_results || {})[cacheKey] || ocrResults[att.filename];
                          const loading = ocrLoading === att.filename;
                          if (cached) {
                            return (
                              <OcrResultPanel
                                key={`ocr-${att.filename}`}
                                attachment={att}
                                result={cached}
                                onConfirmReceive={onPrefilledReceive ? () => {
                                  onPrefilledReceive(cached.detected_items);
                                  onOpenChange(false);
                                } : null}
                              />
                            );
                          }
                          return (
                            <Button
                              key={`ocr-btn-${att.filename}`}
                              size="sm"
                              variant="outline"
                              onClick={() => runOcrMatch(att.filename)}
                              disabled={loading}
                              className="w-full"
                              data-testid={`ocr-match-btn-${att.filename}`}
                            >
                              {loading ? (
                                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                              ) : (
                                <Sparkles className="w-3.5 h-3.5 mr-1.5 text-violet-600" />
                              )}
                              Detectar items con IA · {att.original_name || att.filename}
                            </Button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="border-t pt-4 mt-4">
          {!showForm ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowForm(true)}
              data-testid="thread-add-reply-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Registrar respuesta del proveedor
            </Button>
          ) : (
            <div className="space-y-3">
              <Input
                placeholder="Email del proveedor (opcional)"
                value={fromEmail}
                onChange={(e) => setFromEmail(e.target.value)}
                data-testid="thread-from-email"
              />
              <Textarea
                placeholder="¿Qué te ha dicho el proveedor? (texto, llamada, whatsapp...)"
                rows={4}
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                data-testid="thread-body-text"
              />

              {/* Attachments preview */}
              {attachments.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {attachments.map((att) => {
                    const url = `${BACKEND}${att.url}`;
                    const isImg = (att.content_type || "").startsWith("image/");
                    return (
                      <div
                        key={att.filename}
                        className="relative border rounded overflow-hidden bg-muted group"
                      >
                        {isImg ? (
                          <img src={url} alt={att.original_name} className="w-full h-20 object-cover" />
                        ) : (
                          <div className="w-full h-20 flex flex-col items-center justify-center text-muted-foreground p-2">
                            <FileText className="w-5 h-5" />
                            <span className="text-[10px] truncate w-full text-center mt-1">
                              {att.original_name || att.filename}
                            </span>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removeAttachment(att.filename)}
                          className="absolute top-0.5 right-0.5 bg-black/60 hover:bg-black/80 text-white rounded-full p-0.5"
                          aria-label="Quitar adjunto"
                          data-testid={`thread-remove-attachment-${att.filename}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
                onChange={handleAttachFile}
                className="hidden"
                data-testid="thread-file-input"
              />

              <div className="flex justify-between items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || attachments.length >= 5}
                  data-testid="thread-attach-btn"
                >
                  {uploading ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Paperclip className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Foto del albarán {attachments.length > 0 ? `(${attachments.length})` : ""}
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => { setShowForm(false); setBodyText(""); setFromEmail(""); setAttachments([]); }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="sm"
                    onClick={submitManual}
                    disabled={submitting || uploading}
                    data-testid="thread-submit-btn"
                  >
                    {submitting && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                    Guardar respuesta
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function OcrResultPanel({ attachment, result, onConfirmReceive }) {
  const items = result?.detected_items || [];
  const matchRate = result?.match_rate || 0;
  const matched = result?.matched_count || 0;
  const total = result?.total_detected || 0;
  const totalDiff = result?.total_diff_pct;

  const ratePct = Math.round(matchRate * 100);
  const tone =
    ratePct >= 80 ? "border-emerald-300 bg-emerald-50/40 dark:bg-emerald-950/20" :
    ratePct >= 50 ? "border-amber-300 bg-amber-50/40 dark:bg-amber-950/20" :
                    "border-rose-300 bg-rose-50/40 dark:bg-rose-950/20";

  const StatusIcon = ({ status }) => {
    if (status === "match") return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />;
    if (status === "qty_mismatch") return <AlertCircle className="w-3.5 h-3.5 text-amber-700" />;
    return <XCircle className="w-3.5 h-3.5 text-rose-500" />;
  };

  const fmtEUR = (n) =>
    typeof n === "number"
      ? new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n)
      : "—";

  return (
    <div className={`rounded-lg border p-3 ${tone}`} data-testid={`ocr-result-${attachment.filename}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-3.5 h-3.5 text-violet-600 flex-shrink-0" />
          <p className="font-semibold text-xs">
            IA detectó {total} ítems · {matched} coinciden ({ratePct}%)
          </p>
        </div>
        {typeof totalDiff === "number" && (
          <span className={`text-[10px] font-medium ${totalDiff < 0.05 ? "text-emerald-700" : "text-amber-700"}`}>
            Total: {fmtEUR(result.ocr_total_amount)}
            {totalDiff < 0.05 ? " ✓" : ` · ${(totalDiff * 100).toFixed(1)}% dif`}
          </span>
        )}
      </div>
      <ul className="space-y-1 text-xs">
        {items.slice(0, 8).map((it, idx) => (
          <li key={idx} className="flex items-start gap-2">
            <StatusIcon status={it.match_status} />
            <span className="flex-1 min-w-0">
              <span className="truncate">{it.name}</span>
              <span className="text-muted-foreground ml-1">
                · {it.quantity || it.qty || 1} {it.unit || "ud"}
                {it.match_status === "qty_mismatch" && it.order_qty != null && (
                  <span className="text-amber-700"> (esperado {it.order_qty})</span>
                )}
              </span>
            </span>
          </li>
        ))}
        {items.length > 8 && (
          <li className="text-[11px] text-muted-foreground italic">+{items.length - 8} más</li>
        )}
      </ul>
      {ratePct >= 80 && (
        <p className="mt-2 text-[11px] text-emerald-700 dark:text-emerald-300">
          Coincide con el pedido — puedes confirmar la recepción con seguridad.
        </p>
      )}
      {ratePct < 80 && ratePct >= 50 && (
        <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-300">
          Algunas líneas no coinciden — revisa antes de cerrar la recepción.
        </p>
      )}
      {ratePct < 50 && (
        <p className="mt-2 text-[11px] text-rose-700 dark:text-rose-300">
          La detección no coincide con el pedido. Verifica manualmente o pide aclaración al proveedor.
        </p>
      )}
      {onConfirmReceive && ratePct >= 50 && (
        <Button
          size="sm"
          onClick={onConfirmReceive}
          className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid={`ocr-confirm-receive-${attachment.filename}`}
        >
          <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
          Confirmar recepción con estos datos
        </Button>
      )}
    </div>
  );
}
